import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

import AddOptionDialog from './AddOptionDialog';
import { useAsyncSelectStyles } from './AsyncSelect.styles';

const MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
  transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  getContentAnchorEl: null
};

interface AsyncSelectProps<T extends { id: number; value: string }> {
  data: T[];
  loading: boolean;
  error?: string;
  onRetry: () => void;
  /** Called with the selected item so the parent can store both id and value. */
  // eslint-disable-next-line no-unused-vars
  onChange: (item: { id: number; value: string }) => void;
  label: string;
  /** Return an error string if invalid, or null if valid. Placeholder — always returns null until implemented. */
  // eslint-disable-next-line no-unused-vars
  validate: (val: string) => string | null;
  /** Currently selected option, or null for no selection. The component derives the MUI Select value internally. */
  value: { id: number; value: string } | null;
  /**
   * Called with the typed input on submit. Resolves with the full created
   * item so both id and value are available for auto-selection.
   */
  // eslint-disable-next-line no-unused-vars
  onAdd: (val: string) => Promise<{ id: number; value: string }>;
  /** When provided, renders a remove (−) button inline with the dropdown. */
  onRemove?: () => void;
  /** Whether this item can be removed. Defaults to true. */
  deletable?: boolean;
}

/**
 * A self-contained async-populated Select that handles loading, error+retry,
 * and adding new options via a dialog. Must be used with MUI v4.
 *
 * Note on MUI v4 Select internals: renderSelectContent must be called as a
 * plain function (not rendered as <Component />) so MUI can clone the returned
 * MenuItem nodes directly and wire its onChange handler.
 */
const AsyncSelect = <T extends { id: number; value: string }>({
  data,
  loading,
  error = undefined,
  onRetry,
  onAdd,
  onChange,
  label,
  validate,
  value,
  onRemove = undefined,
  deletable = true
}: AsyncSelectProps<T>) => {
  const classes = useAsyncSelectStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    if (!event.target.value || typeof event.target.value !== 'string') return;
    const item = data.find(d => String(d.id) === event.target.value);
    if (item) onChange({ id: item.id, value: item.value });
  };

  const renderSelectContent = () => {
    if (loading) {
      return (
        <MenuItem disabled>
          <div className={classes.menuLoading}>
            <CircularProgress size={20} />
          </div>
        </MenuItem>
      );
    }
    if (error) {
      return (
        <MenuItem disabled>
          <div className={classes.menuError}>
            <Typography variant='body2' color='error' gutterBottom>
              {error}
            </Typography>
            <Button
              size='small'
              variant='outlined'
              className={classes.retryButton}
              onClick={e => {
                e.stopPropagation();
                onRetry();
              }}>
              Retry
            </Button>
          </div>
        </MenuItem>
      );
    }
    if (data.length === 0) {
      return (
        <MenuItem disabled>
          <Typography variant='body2'>No options available</Typography>
        </MenuItem>
      );
    }
    return data.map(option => (
      <MenuItem key={option.id} value={String(option.id)} className={classes.dropdownText}>
        {option.value}
      </MenuItem>
    ));
  };

  // MUI Select needs a primitive value; we derive it here so the parent never has to.
  const selectValue = value ? String(value.id) : '';

  const renderSelectValue = (selected: unknown): React.ReactNode => {
    if (!selected) return <span className={classes.dropdownPlaceholder}>Select {label}</span>;
    // value.value is the display string — no data lookup required.
    return value?.value ?? String(selected);
  };

  return (
    <div className={classes.container}>
      <div className={classes.selectRow}>
        <Select
          variant='standard'
          disableUnderline
          required
          displayEmpty
          MenuProps={MENU_PROPS}
          value={selectValue}
          onChange={handleSelectChange}
          className={classes.dropdownContainer}
          inputProps={{ classes: { icon: classes.dropdownIcon } }}
          renderValue={renderSelectValue}>
          {renderSelectContent()}
        </Select>
        {onRemove && deletable && (
          <IconButton
            size='small'
            onClick={onRemove}
            className={classes.removeButton}
            title='Remove'>
            <RemoveCircleOutlineIcon fontSize='small' />
          </IconButton>
        )}
      </div>

      <div className={classes.addRow}>
        <Typography variant='body2' className={classes.addHint}>
          Not in the drop-down?
        </Typography>
        <Button size='small' className={classes.addButton} onClick={() => setDialogOpen(true)}>
          Add a Custom {label}
        </Button>
      </div>

      <AddOptionDialog
        open={dialogOpen}
        label={label}
        onClose={() => setDialogOpen(false)}
        validate={validate}
        onAdd={onAdd}
        onSuccess={onChange}
      />
    </div>
  );
};

AsyncSelect.defaultProps = { error: undefined, onRemove: undefined, deletable: true };

export default AsyncSelect;
